import React, { FC, Fragment } from 'react';
import { Card, CardActions, CardContent, Skeleton, Typography } from '@mui/material';
import DisabledCardState from '@components/Cards/DisabledCardState';
import { useUiState } from '@utils/UiStateHook';
import { usePotCard2Controller } from '@components/PotCard/UsePotCard2Controller';
import { DateTime } from 'luxon';
import { ToDate } from '@utils/DateUtils';
import { InfoBubble } from '../InfoBubble';

export interface PotCard2Props {
  groupId: number;
  groupStartDate: string | DateTime;
}

export const PotCard2: FC<PotCard2Props> = ({ groupId, groupStartDate }) => {
  const { potValue, pledgeAmount, remainingPledgeAmount, disabledMessage, isLoading, enabled } = usePotCard2Controller(
    groupId,
    ToDate(groupStartDate)
  );
  const uiState = useUiState(enabled);
  const backgroundStyles = {
    border: '1px solid',
    borderColor: uiState.backgroundColor,
    borderRadius: 16,
  };
  return (
    <Fragment>
      {isLoading && (
        <Card style={backgroundStyles} variant={'elevation'}>
          <CardContent>
            <Typography variant={'h4'} component={'p'}>
              <Skeleton width="80%" />
            </Typography>
            <Typography variant={'h3'} component={'p'}>
              <Skeleton width="60%" />
            </Typography>
          </CardContent>
          <CardActions sx={{ justifyContent: 'end', height: '90%' }}>
            <Skeleton variant="rectangular" width={100} height={40} />
          </CardActions>
        </Card>
      )}
      {!isLoading && (
        <Card style={backgroundStyles} variant={'elevation'}>
          <CardContent>
            <div
              className="title-with-info"
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 6, // space between title and info icon
                flexWrap: 'nowrap',
              }}
            >
              <Typography variant="h5" component="div" sx={{ color: uiState.color }}>
                Pledge Pot
              </Typography>
              <InfoBubble title="My Pledge Pot">
                Everyone begins the program with the same initial pledge—either self-funded or sponsored by an outside
                organization. Each week you don&#39;t meet your goal, one-quarter of your pledge moves into the group
                pledge pot. As more people miss weeks, the group pot grows. At the end of the program, that pot becomes
                the prize and celebration fund for everyone.
              </InfoBubble>
            </div>
            {!enabled && (
              <DisabledCardState disabledMessage={disabledMessage} src="/assets/svg/pot.svg" alt="Get stoked!" />
            )}
            {enabled && (
              <div className="card-content-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <img src={'/assets/svg/pot.svg'} alt={'Pot'} className="card-logo" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                  <Typography component={'p'}>My Initial Pledge: {pledgeAmount}</Typography>
                  <Typography component={'p'}>My Remaining Pledge: {remainingPledgeAmount}</Typography>
                  <Typography component={'p'}>Group Pledge Pot Total: {potValue}</Typography>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </Fragment>
  );
};
